import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../api/client';

export interface ClearPhaseStartVars {
  jobId: string;
  phase: 'wash' | 'run';
  /** For the toast — the job number the office is looking at. */
  jobNumber?: string;
}

/**
 * Clear a wrongly recorded phase start (DELETE /dispatch/jobs/:id/phase/:phase/start,
 * api#1028). Office only — gate the control on `canClearPhaseStart`.
 *
 * The live case is TNT J-4372: a same-day Start tap stamped the run with the
 * wash's instant, the run then moved three days out, and the board kept it on
 * the start day because a started phase is drawn on its START day. Clearing the
 * start puts the run back on its scheduled day.
 *
 * The API's 409 sentence ("already ended", "no start recorded") is better than
 * anything written here, so it is shown verbatim. The job's status is NOT
 * changed by a clear; whether it goes back to pending is the edit form's call.
 */
export function useClearPhaseStartMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, phase }: ClearPhaseStartVars) => api.dispatch.clearPhaseStart(jobId, phase),
    onSuccess: (_res, { phase, jobNumber }) => {
      toast.success(`${phase === 'wash' ? 'Wash' : 'Run'} start cleared${jobNumber ? ` on ${jobNumber}` : ''}.`);
    },
    onError: (err, { phase, jobNumber }) => {
      toast.error(getApiErrorMessage(err, `Could not clear the ${phase} start${jobNumber ? ` on ${jobNumber}` : ''}.`));
    },
    onSettled: (_res, _err, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['field-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['field-my-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['field-dashboard'] });
    },
  });
}
